/**
 * Witness cases for the Integrity Dashboard.
 * Each case has a buildConfig to generate synthetic rows and an expediente
 * to drive the engine, plus golden output values from a known-good run.
 *
 * Golden values are captured by running the full pipeline once and recording
 * the output fields. Update them deliberately when the engine changes.
 * See: scripts/compute-golden-values.test.js
 */

export const CASOS_TESTIGO = [
  {
    id: 'dependencia-estable',
    label: 'Dependencia Estable',
    description: 'Aportante en relación de dependencia, 240 meses continuos, cese reciente.',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 420000, sexo: 'M' },
    buildConfig: { base: 80000, months: 240, startYear: 2006, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 446350.09,
      retroactivoTotal: 8860954.83,
      pbu: 363000,
      pc: 0,
      pap: 83350.09,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 6.27,
    },
  },
  {
    id: 'general-mixto',
    label: 'General Mixto',
    description: 'Relación de dependencia con algunos períodos como autónomo.',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 450000, sexo: 'M' },
    buildConfig: { base: 80000, months: 240, startYear: 2006, startMonth: 1, tipoActividad: 'autonomo' },
    golden: {
      haberBolsilloReajustado: 446309.3,
      retroactivoTotal: 0,
      pbu: 363000,
      pc: 0,
      pap: 83309.3,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'GENERAL_MIXTO',
      formulaPath: 'general',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'docente-puro',
    label: 'Docente Puro',
    description: 'Docente con toda su carrera en actividad docente.',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 380000, sexo: 'F', esDocente: true },
    buildConfig: { base: 75000, months: 240, startYear: 2006, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 0,
      retroactivoTotal: 0,
      pbu: 363000,
      pc: 0,
      pap: 0,
      movilidadAcumulada: 1.021,
      divisorPromedioAplicado: 120,
      scenarioKey: 'DOCENTE_PURO',
      formulaPath: 'docente',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'docente-mixto',
    label: 'Docente Mixto',
    description: 'Docente con períodos mixtos (docencia + autónomo).',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 420000, sexo: 'F', esDocente: true },
    buildConfig: { base: 82000, months: 240, startYear: 2006, startMonth: 1, tipoActividad: 'autonomo' },
    golden: {
      haberBolsilloReajustado: 446309.3,
      retroactivoTotal: 8860954.83,
      pbu: 363000,
      pc: 0,
      pap: 83309.3,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'DOCENTE_MIXTO',
      formulaPath: 'general',
      incrementoMensualPct: 6.26,
    },
  },
  {
    id: 'pension-derivada',
    label: 'Pensión Derivada',
    description: 'Pensión derivada por fallecimiento del causante.',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 350000, sexo: 'F', datosPension: { tipoPension: 'DERIVADA', fechaFallecimiento: '2024-01-15' } },
    buildConfig: { base: 80000, months: 240, startYear: 2006, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 0,
      retroactivoTotal: 0,
      pbu: 363000,
      pc: 0,
      pap: 83350.09,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'PENSION_DERIVADA',
      formulaPath: 'pension',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'pension-directa',
    label: 'Pensión Directa',
    description: 'Pensión directa (el titular falleció estando en actividad).',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 400000, sexo: 'F', datosPension: { tipoPension: 'DIRECTA', fechaFallecimiento: '2025-06-01' } },
    buildConfig: { base: 85000, months: 120, startYear: 2016, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 116787.76,
      retroactivoTotal: 0,
      pbu: 363000,
      pc: 0,
      pap: 23116.74,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'PENSION_DIRECTA',
      formulaPath: 'pension',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'ama-de-casa',
    label: 'Ama de Casa',
    description: 'Aportante mujer con hijos, menos años de aportes, necesita moratoria.',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 280000, sexo: 'F', cantidadHijos: 3 },
    buildConfig: { base: 60000, months: 72, startYear: 2016, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 376020.31,
      retroactivoTotal: 3951317.53,
      pbu: 363000,
      pc: 0,
      pap: 13020.31,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 72,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 34.29,
    },
  },
  {
    id: 'simultaneidad',
    label: 'Simultaneidad',
    description: 'Aportes simultáneos (múltiples CUIT en el mismo período).',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 500000, sexo: 'M' },
    buildConfig: { base: 90000, months: 120, startYear: 2016, startMonth: 1, simultaneous: true },
    golden: {
      haberBolsilloReajustado: 386116.74,
      retroactivoTotal: 0,
      pbu: 363000,
      pc: 0,
      pap: 23116.74,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'cese-antiguo',
    label: 'Cese Antiguo',
    description: 'Fecha de cese anterior (2017) — activa puente de movilidad.',
    expediente: { fechaCese: '2017-03-01', fallo: 'RIPTE', haberActual: 320000, sexo: 'M' },
    buildConfig: { base: 55000, months: 120, startYear: 2006, startMonth: 3 },
    golden: {
      haberBolsilloReajustado: 210050.47,
      retroactivoTotal: 0,
      pbu: 180300,
      pc: 0,
      pap: 29750.47,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'cese-reciente',
    label: 'Cese Reciente',
    description: 'Fecha de cese muy reciente (2026) — sin puente de movilidad.',
    expediente: { fechaCese: '2026-06-01', fallo: 'RIPTE', haberActual: 480000, sexo: 'M' },
    buildConfig: { base: 85000, months: 120, startYear: 2016, startMonth: 4 },
    golden: {
      haberBolsilloReajustado: 394918.66,
      retroactivoTotal: 0,
      pbu: 372000,
      pc: 0,
      pap: 22918.66,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'confiscatoriedad',
    label: 'Confiscatoriedad',
    description: 'Base muy alta (420000) — debería producir quita.',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 420000, sexo: 'M' },
    buildConfig: { base: 420000, months: 240, startYear: 2006, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 446350.09,
      retroactivoTotal: 8860954.83,
      pbu: 363000,
      pc: 0,
      pap: 83350.09,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 6.27,
    },
  },
  {
    id: 'construccion',
    label: 'Construcción (UOCRA)',
    description: 'Régimen especial de construcción.',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 450000, sexo: 'M', regimen: 'UOCRA', rubro: 'construccion' },
    buildConfig: { base: 95000, months: 240, startYear: 2006, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 446350.09,
      retroactivoTotal: 0,
      pbu: 363000,
      pc: 0,
      pap: 83350.09,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'ESPECIAL_REGIMEN',
      formulaPath: 'general',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'base-alta-topes',
    label: 'Base Alta con Topes',
    description: 'Base muy alta (520000) con 120 meses.',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 520000, sexo: 'M' },
    buildConfig: { base: 520000, months: 120, startYear: 2016, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 386116.74,
      retroactivoTotal: 0,
      pbu: 363000,
      pc: 0,
      pap: 23116.74,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 0,
    },
  },
  {
    id: 'historial-corto',
    label: 'Historial Corto',
    description: 'Menos de 60 meses de aportes (58).',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 250000, sexo: 'M' },
    buildConfig: { base: 70000, months: 58, startYear: 2020, startMonth: 6 },
    golden: {
      haberBolsilloReajustado: 371286.18,
      retroactivoTotal: 1016216.56,
      pbu: 363000,
      pc: 0,
      pap: 8286.18,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 58,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 48.51,
    },
  },
  {
    id: 'base-media-cese-reciente',
    label: 'Base Media, Cese Reciente',
    description: 'Base media (134000) con cese reciente (2026-03).',
    expediente: { fechaCese: '2026-03-01', fallo: 'RIPTE', haberActual: 490000, sexo: 'M' },
    buildConfig: { base: 134000, months: 120, startYear: 2016, startMonth: 1 },
    golden: {
      haberBolsilloReajustado: 386116.74,
      retroactivoTotal: 0,
      pbu: 363000,
      pc: 0,
      pap: 23116.74,
      movilidadAcumulada: 1,
      divisorPromedioAplicado: 120,
      scenarioKey: 'GENERAL_DEPENDENCIA',
      formulaPath: 'general',
      incrementoMensualPct: 0,
    },
  },
]
